import { BookOpen, Cake, Megaphone, Mic2 } from 'lucide-react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { ChartCard } from '@/components/shared/chart-card';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { SimpleDonutChart } from '@/components/shared/simple-donut-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCompactMoney, formatMoney, formatNumber, formatTrendDate } from '@/utils/format';

import { DashboardCalendarTile } from './dashboard-calendar-tile';
import {
  formatDashboardChange,
  METRIC_TIP_IDS,
  type DashboardResponse,
} from './dashboard-content';
import { DASHBOARD_METRIC_ICONS } from './dashboard-metric-icons';
import {
  DashboardContentPreviewList,
  DashboardFollowUpPreviewList,
  DashboardJoinRequestPreviewList,
  DashboardPersonPreviewList,
} from './dashboard-preview-rows';
import { DashboardSectionCard } from './dashboard-section-card';

const personName = (firstName: string, lastName: string) =>
  [firstName, lastName].filter(Boolean).join(' ');

const formatBirthdayDay = (iso: string, locale: string): string => {
  const parsed = DateTime.fromISO(iso, { zone: 'utc' }).setLocale(locale);
  if (!parsed.isValid) return '—';
  return parsed.toFormat('MMM d');
};

export const DashboardWidgetPreview = ({
  widgetKey,
  dashboard,
  loading,
  baseCurrency,
  locale,
  compact = false,
}: {
  widgetKey: string;
  dashboard?: DashboardResponse;
  loading: boolean;
  baseCurrency: string;
  locale: string;
  compact?: boolean;
}) => {
  const { t } = useTranslation('reports');

  const categoryLabel = (key: string) => {
    const translated = t(`value.${key}`, { defaultValue: '' });
    return translated || key;
  };

  const metric = dashboard?.metrics.find((row) => row.key === widgetKey);
  if (metric) {
    const previewFooter = compact
      ? null
      : metricPreviewFooter(widgetKey, dashboard, locale);
    return (
      <StatCard
        className={compact ? 'h-full' : undefined}
        label={t(metric.nameKey)}
        value={
          metric.value === null || metric.value === undefined
            ? undefined
            : formatNumber(metric.value)
        }
        money={
          metric.money
            ? { amount: metric.money.amountMinor, currency: metric.money.currency }
            : undefined
        }
        icon={DASHBOARD_METRIC_ICONS[metric.key]}
        comparison={
          metric.comparison
            ? {
                change: metric.comparison.change,
                label: t('dashboard.vsPreviousPeriod'),
                formatted: formatDashboardChange(metric.comparison.change),
              }
            : undefined
        }
        tipId={METRIC_TIP_IDS[metric.key]}
        loading={loading}
        footer={previewFooter}
      />
    );
  }

  const charts = dashboard?.charts;
  const chartHeight = compact ? 72 : 140;
  const donutSize = compact ? 72 : 120;
  const chartCardClass = compact
    ? 'flex h-full flex-col overflow-hidden [&>div:last-child]:min-h-0 [&>div:last-child]:flex-1'
    : undefined;
  const barPreviewLimit = compact ? 3 : undefined;

  if (widgetKey === 'chart_giving_trend') {
    return (
      <ChartCard
        className={chartCardClass}
        title={t('dashboard.charts.givingTrend')}
        loading={loading}
        hasData={(charts?.givingTrend.length ?? 0) > 0}
        empty={{ title: t('dashboard.charts.givingTrendEmpty') }}
        height={chartHeight}
      >
        <SimpleBarChart
          data={(charts?.givingTrend ?? []).map((point) => ({
            label: formatTrendDate(point.label, point.grain),
            value: point.value,
          }))}
          formatValue={(value) => formatMoney(value, baseCurrency, { showSymbol: true })}
          formatTick={(value) => formatCompactMoney(value, baseCurrency)}
          height={chartHeight - 16}
        />
      </ChartCard>
    );
  }

  if (widgetKey === 'chart_giving_by_fund') {
    const fundData = charts?.givingByFund ?? [];
    const previewData = barPreviewLimit ? fundData.slice(0, barPreviewLimit) : fundData;

    return (
      <ChartCard
        className={chartCardClass}
        title={t('dashboard.charts.givingByFund')}
        loading={loading}
        hasData={fundData.length > 0}
        empty={{ title: t('dashboard.charts.givingByFundEmpty') }}
        height={chartHeight}
      >
        <SimpleBarChart
          orientation="horizontal"
          data={previewData.map((point) => ({
            label: point.label,
            value: point.value,
            currency: point.currency ?? baseCurrency,
          }))}
          formatValue={(value, item) =>
            formatMoney(value, item?.currency ?? baseCurrency, { showSymbol: true })
          }
          height={chartHeight - 20}
        />
      </ChartCard>
    );
  }

  if (widgetKey === 'chart_members_by_gender') {
    return (
      <ChartCard
        className={chartCardClass}
        title={t('dashboard.charts.membersByGender')}
        loading={loading}
        hasData={(charts?.membersByGender.length ?? 0) > 0}
        empty={{ title: t('dashboard.charts.breakdownEmpty') }}
        height={chartHeight}
      >
        <SimpleDonutChart
          data={(charts?.membersByGender ?? []).map((point) => ({
            label: categoryLabel(point.key),
            value: point.value,
          }))}
          formatValue={(value) => formatNumber(value)}
          centerLabel={t('dashboard.activeMembers')}
          centerValue={formatNumber(
            (charts?.membersByGender ?? []).reduce((sum, point) => sum + point.value, 0),
          )}
          size={donutSize}
        />
      </ChartCard>
    );
  }

  if (widgetKey === 'chart_members_by_type') {
    return (
      <ChartCard
        className={chartCardClass}
        title={t('dashboard.charts.membersByType')}
        loading={loading}
        hasData={(charts?.membersByType.length ?? 0) > 0}
        empty={{ title: t('dashboard.charts.breakdownEmpty') }}
        height={chartHeight}
      >
        <SimpleDonutChart
          data={(charts?.membersByType ?? []).map((point) => ({
            label: categoryLabel(point.key),
            value: point.value,
          }))}
          formatValue={(value) => formatNumber(value)}
          centerLabel={t('dashboard.activeMembers')}
          centerValue={formatNumber(
            (charts?.membersByType ?? []).reduce((sum, point) => sum + point.value, 0),
          )}
          size={donutSize}
        />
      </ChartCard>
    );
  }

  if (widgetKey === 'chart_members_by_group') {
    const autoFilledData = charts?.membersByAutoFilledGroup ?? [];
    const manualData = charts?.membersByManualGroup ?? [];
    const previewAutoFilled = barPreviewLimit
      ? autoFilledData.slice(0, barPreviewLimit)
      : autoFilledData;
    const previewManual = barPreviewLimit ? manualData.slice(0, barPreviewLimit) : manualData;

    return (
      <Card className={chartCardClass}>
        <CardContent className="grid grid-cols-2 items-start gap-4 p-5 max-sm:grid-cols-1">
          <section className="min-w-0">
            <p className="text-card-title font-semibold text-fg">
              {t('dashboard.charts.membersByManualGroup')}
            </p>
            <div className="mt-3" style={{ minHeight: chartHeight }}>
              {loading ? (
                <p className="text-body-sm text-fg-muted">…</p>
              ) : manualData.length === 0 ? (
                <p className="text-body-sm text-fg-muted">{t('dashboard.charts.breakdownEmpty')}</p>
              ) : (
                <SimpleBarChart
                  orientation="horizontal"
                  data={previewManual.map((point) => ({
                    label: point.label,
                    value: point.value,
                  }))}
                  formatValue={(value) => formatNumber(value)}
                  height={chartHeight - 20}
                />
              )}
            </div>
          </section>
          <section className="min-w-0">
            <p className="text-card-title font-semibold text-fg">
              {t('dashboard.charts.membersByAutoFilledGroup')}
            </p>
            <div className="mt-3" style={{ minHeight: chartHeight }}>
              {loading ? (
                <p className="text-body-sm text-fg-muted">…</p>
              ) : autoFilledData.length === 0 ? (
                <p className="text-body-sm text-fg-muted">{t('dashboard.charts.breakdownEmpty')}</p>
              ) : (
                <SimpleBarChart
                  orientation="horizontal"
                  data={previewAutoFilled.map((point) => ({
                    label: point.label,
                    value: point.value,
                  }))}
                  formatValue={(value) => formatNumber(value)}
                  height={chartHeight - 20}
                />
              )}
            </div>
          </section>
        </CardContent>
      </Card>
    );
  }

  if (widgetKey === 'calendar') {
    return (
      <DashboardCalendarTile
        month={dashboard?.calendar?.month ?? ''}
        days={dashboard?.calendar?.days ?? []}
        loading={loading}
        compact={compact}
      />
    );
  }

  if (widgetKey === 'recent_sermons') {
    const items = dashboard?.content?.sermons ?? [];
    return (
      <DashboardSectionCard
        title={t('dashboard.content.sermons')}
        icon={<Mic2 className="size-4 text-fg-tertiary" aria-hidden />}
        loading={loading}
        empty={t('dashboard.content.sermonsEmpty')}
        hasItems={items.length > 0}
        compact={compact}
      >
        <DashboardContentPreviewList
          items={items.slice(0, 3)}
          locale={locale}
          to="/content/sermons"
        />
      </DashboardSectionCard>
    );
  }

  if (widgetKey === 'recent_announcements') {
    const items = dashboard?.content?.announcements ?? [];
    return (
      <DashboardSectionCard
        title={t('dashboard.content.announcements')}
        icon={<Megaphone className="size-4 text-fg-tertiary" aria-hidden />}
        loading={loading}
        empty={t('dashboard.content.announcementsEmpty')}
        hasItems={items.length > 0}
        compact={compact}
      >
        <DashboardContentPreviewList
          items={items.slice(0, 3)}
          locale={locale}
          to="/content/announcements"
        />
      </DashboardSectionCard>
    );
  }

  if (widgetKey === 'recent_devotionals') {
    const items = dashboard?.content?.devotionals ?? [];
    return (
      <DashboardSectionCard
        title={t('dashboard.content.devotionals')}
        icon={<BookOpen className="size-4 text-fg-tertiary" aria-hidden />}
        loading={loading}
        empty={t('dashboard.content.devotionalsEmpty')}
        hasItems={items.length > 0}
        compact={compact}
      >
        <DashboardContentPreviewList
          items={items.slice(0, 3)}
          locale={locale}
          to="/content/devotionals"
        />
      </DashboardSectionCard>
    );
  }

  if (widgetKey === 'birthdays') {
    const today = dashboard?.birthdays.today ?? [];
    const thisMonth = dashboard?.birthdays.thisMonth ?? [];
    const previewPeople = today.length > 0 ? today : thisMonth.slice(0, 3);

    return (
      <Card
        className={
          compact
            ? 'flex h-full flex-col overflow-hidden [&>div:last-child]:min-h-0 [&>div:last-child]:flex-1'
            : undefined
        }
      >
        <CardHeader className={compact ? 'p-3 pb-2' : 'pb-2'}>
          <CardTitle className="flex items-center gap-2 text-card-title">
            <Cake className="size-4 text-fg-tertiary" aria-hidden />
            {t('dashboard.birthdays.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className={compact ? 'flex-1 overflow-hidden' : undefined}>
          {loading ? (
            <p className="text-body-sm text-fg-muted">…</p>
          ) : previewPeople.length === 0 ? (
            <p className="text-body-sm text-fg-muted">{t('dashboard.birthdays.todayEmpty')}</p>
          ) : (
            <ul className="space-y-1.5">
              {previewPeople.map((person) => (
                <li
                  key={person.id}
                  className="flex items-center justify-between gap-3 text-body-sm text-fg"
                >
                  <span>{personName(person.firstName, person.lastName)}</span>
                  {today.length === 0 ? (
                    <span className="shrink-0 text-caption text-fg-tertiary tabular-nums">
                      {formatBirthdayDay(person.dateOfBirth, locale)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
};

const metricPreviewFooter = (
  widgetKey: string,
  dashboard: DashboardResponse | undefined,
  locale: string,
) => {
  const previews = dashboard?.previews;
  if (!previews) return null;

  if (widgetKey === 'new_members_in_period' && previews.newMembers.length > 0) {
    return (
      <DashboardPersonPreviewList
        people={previews.newMembers.slice(0, 3)}
        locale={locale}
        showPhoto={false}
      />
    );
  }
  if (widgetKey === 'first_timers_in_period' && previews.firstTimers.length > 0) {
    return (
      <DashboardPersonPreviewList
        people={previews.firstTimers.slice(0, 3)}
        locale={locale}
        showPhoto={false}
      />
    );
  }
  if (widgetKey === 'open_follow_ups' && previews.openFollowUps.length > 0) {
    return (
      <DashboardFollowUpPreviewList items={previews.openFollowUps.slice(0, 3)} locale={locale} />
    );
  }
  if (widgetKey === 'pending_member_requests' && previews.pendingMemberRequests.length > 0) {
    return <DashboardJoinRequestPreviewList items={previews.pendingMemberRequests.slice(0, 3)} />;
  }
  return null;
};
